/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */
import {Dispatch} from 'redux';

import {fetchRooms, fetchRoomMembers, fetchRoomDetails} from 'services/room.service';
import {
  IRoom,
  ISelectItem,
  IMember
} from 'interfaces';

import {IAppStore} from 'store';
import {IPortalError, transformToPortalError} from 'utility/error-handler';

export const ROOMS_FETCHING = 'ROOMS_FETCHING' as const;
export const ROOMS_FETCHING_DONE = 'ROOMS_FETCHING_DONE' as const;
export const ROOMS_FETCHING_ERROR = 'ROOMS_FETCHING_ERROR' as const;
export const ROOMS_REMOVE = 'ROOMS_REMOVE' as const;

export const SET_ROOM_TYPE = 'SET_ROOM_TYPE' as const;

export const ROOM_DETAILS_FETCHING = 'ROOM_DETAILS_FETCHING' as const;
export const ROOM_DETAILS_FETCHING_DONE = 'ROOM_DETAILS_FETCHING_DONE' as const;
export const ROOM_DETAILS_FETCHING_ERROR = 'ROOM_DETAILS_FETCHING_ERROR' as const;

export const ROOM_MEMBERS_FETCHING = 'ROOM_MEMBERS_FETCHING' as const;
export const ROOM_MEMBERS_FETCHING_DONE = 'ROOM_MEMBERS_FETCHING_DONE' as const;
export const ROOM_MEMBERS_FETCHING_ERROR = 'ROOM_MEMBERS_FETCHING_ERROR' as const;

export const roomsSelector = ({rooms}: IAppStore): IAppStore['rooms'] => rooms;
export const roomDetailsSelector = ({roomDetails}: IAppStore): IAppStore['roomDetails'] => roomDetails;
export const roomMembersSelector = ({roomMembers}: IAppStore): IAppStore['roomMembers'] => roomMembers;

interface IRequestRoomsAction {
  type: typeof ROOMS_FETCHING;
}
interface IReceiveRoomsAction {
  type: typeof ROOMS_FETCHING_DONE;
  payload: IRoom[];
}
interface IFailedRoomsAction {
  type: typeof ROOMS_FETCHING_ERROR;
  error: null | IPortalError;
}
export type FetchingRoomsActions =
  | IRequestRoomsAction
  | IReceiveRoomsAction
  | IFailedRoomsAction;
interface IGetRoomsActions {
  request: () => IRequestRoomsAction;
  receive: (payload: IRoom[]) => IReceiveRoomsAction;
  failed: (error: null | IPortalError) => IFailedRoomsAction;
}
const getRoomsActions: IGetRoomsActions = {
  request: () => ({type: ROOMS_FETCHING}),
  receive: payload => ({
    type: ROOMS_FETCHING_DONE,
    payload
  }),
  failed: error => ({
    type: ROOMS_FETCHING_ERROR,
    error
  })
};

export const listRooms = (type?: string) => async(dispatch: Dispatch<FetchingRoomsActions>): Promise<void> => {
  const {receive, request, failed} = getRoomsActions;

  dispatch(request());

  try {
    const rooms: IRoom[] = await fetchRooms(type);

    dispatch(receive(rooms));
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};

interface IRemoveRoomAction {
  type: typeof ROOMS_REMOVE;
  roomId: string;
}
export const removeRoom: (roomId: string) => IRemoveRoomAction = (roomId: string) => ({
  type: ROOMS_REMOVE,
  roomId
});

interface ISetRoomTypeAction {
  type: typeof SET_ROOM_TYPE;
  roomType?: ISelectItem;
}
export const setRoomType: (roomType?: ISelectItem) => ISetRoomTypeAction = (roomType?: ISelectItem) => ({
  type: SET_ROOM_TYPE,
  roomType
});

interface IFetchRoomDetailsRequest {
  type: typeof ROOM_DETAILS_FETCHING;
}

interface IFetchRoomDetailsSuccess {
  type: typeof ROOM_DETAILS_FETCHING_DONE;
  payload: IRoom;
}

interface IFetchRoomDetailsFailed {
  type: typeof ROOM_DETAILS_FETCHING_ERROR;
  error: null | IPortalError;
}

export type FetchRoomDetailsActions =
  | IFetchRoomDetailsRequest
  | IFetchRoomDetailsSuccess
  | IFetchRoomDetailsFailed;

interface IFetchRoomDetailsActions {
  request: () => IFetchRoomDetailsRequest;
  receive: (payload: IRoom) => IFetchRoomDetailsSuccess;
  failed: (error: null | IPortalError) => IFetchRoomDetailsFailed;
}

const fetchRoomDetailsActions: IFetchRoomDetailsActions = {
  request: () => ({type: ROOM_DETAILS_FETCHING}),
  receive: payload => ({
    type: ROOM_DETAILS_FETCHING_DONE,
    payload
  }),
  failed: error => ({
    type: ROOM_DETAILS_FETCHING_ERROR,
    error
  })
};

export const getRoomDetails = (roomId: string) => async(dispatch: Dispatch): Promise<void> => {
  const {receive, request, failed} = fetchRoomDetailsActions;

  dispatch(request());

  try {
    const room: IRoom = await fetchRoomDetails(roomId);

    dispatch(receive(room));
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};

interface IFetchRoomMembersRequest {
  type: typeof ROOM_MEMBERS_FETCHING;
}

interface IFetchRoomMembersSuccess {
  type: typeof ROOM_MEMBERS_FETCHING_DONE;
  payload: IMember[];
}

interface IFetchRoomMembersFailed {
  type: typeof ROOM_MEMBERS_FETCHING_ERROR;
  error: null | IPortalError;
}

export type FetchRoomMembersActions =
  | IFetchRoomMembersRequest
  | IFetchRoomMembersSuccess
  | IFetchRoomMembersFailed;

interface IFetchRoomMembersActions {
  request: () => IFetchRoomMembersRequest;
  receive: (payload: IMember[]) => IFetchRoomMembersSuccess;
  failed: (error: null | IPortalError) => IFetchRoomMembersFailed;
}

const fetchRoomMembersActions: IFetchRoomMembersActions = {
  request: () => ({type: ROOM_MEMBERS_FETCHING}),
  receive: payload => ({
    type: ROOM_MEMBERS_FETCHING_DONE,
    payload
  }),
  failed: error => ({
    type: ROOM_MEMBERS_FETCHING_ERROR,
    error
  })
};

export const getRoomMembers = (roomId: string) => async(dispatch: Dispatch): Promise<void> => {
  const {receive, request, failed} = fetchRoomMembersActions;

  dispatch(request());

  try {
    const roomMembers: IMember[] = await fetchRoomMembers(roomId);

    dispatch(receive(roomMembers));
  } catch (e) {
    const portalError = transformToPortalError(e);

    dispatch(failed(portalError));

    throw portalError;
  }
};

export type RoomsActions =
FetchingRoomsActions
| IRemoveRoomAction
| ISetRoomTypeAction
| FetchRoomDetailsActions
| FetchRoomMembersActions;